/**
 * An impact assessment - its own record, not a recovery set (14 Oct 2026): what is compromised,
 * what it impacts, the scenarios, every object, what to request and its history
 * (backend/src/lib/impact-assessment.js, impact-assessment-record.js; /api/impact-assessments).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api/axios'

export interface ImpactScopeItem {
    id: number
    name: string
    category?: string | null
    /** Objects of the set this covers, and how many of those are marked compromised. */
    objects: number
    compromised: number
    /** Ticked as hit, and by whom. */
    ticked: boolean
    tickedBy?: string | null
    tickedAt?: string | null
}
export interface ImpactScope {
    assessmentId: number
    objects: number
    compromised: number
    withoutLocation: number
    /** Objects marked compromised one by one: no ticked location or platform accounts for them. */
    markedObjects?: Array<{ objectId: string; name: string; locationName: string | null }>
    locations: ImpactScopeItem[]
    /** The set's applications, named one by one: an application is marked itself, service or not. */
    applications: Array<{ objectId: string; name: string; isService: boolean; compromised: boolean }>
    classifications: ImpactScopeItem[]
    technologies: ImpactScopeItem[]
}
export interface ImpactSelection { locationIds: number[]; classificationIds: number[]; technologyIds: number[]; objectIds: string[] }

/** Why a part cannot go where the scenario sends it (backend lib/estate-parts.js). */
export type PartReasonCode = 'part_destination_hit' | 'connection_hit' | 'part_not_recovered'
export interface PartReason { code: PartReasonCode; part: string; to?: string; locations?: string[] }
export type ImpactTargetStatus = 'within_target' | 'past_target' | 'past_maximum' | 'no_target'
/** after_cleaning: rebuilt in place once a lost location is cleaned. no_hierarchy: affected in production, but not in this scenario's hierarchy. */
export type ImpactState = 'operating' | 'recoverable' | 'recoverable_with_gaps' | 'after_cleaning' | 'blocked' | 'unknown' | 'no_hierarchy'
export interface ImpactServiceOutcome {
    objectId: string
    description: string
    state: ImpactState
    reason: null | { code: 'location_hit' | 'destination_hit' | 'no_recovery_point' | 'scenario_not_stated' | 'dependency_hit' | PartReasonCode | 'clean_first' | 'untouched_here'; locations?: string[]; locationIds?: number[]; objects?: string[]; count?: number; part?: string; parts?: string[]; to?: string; cleanSeconds?: number | null }
    /** Parts that only recover through their fallback. */
    fallbacks?: string[]
    objectsBeneath: number
    compromisedBeneath: number
    /** The impacted applications beneath it, by name: the route from what was hit. Absent when it is operating. */
    via?: string[]
    /** Seconds. */
    rolledUpRto: number | null
    /** The customer's targets for this service, in seconds, and where its recovery time falls. Absent when it is operating. */
    targetRtoSeconds?: number | null
    targetMtpdSeconds?: number | null
    targetStatus?: ImpactTargetStatus
    /** Only when asked for (the Recovery detail view), and only for a service that is not operating. */
    detail?: ImpactServiceDetail
}
export interface ImpactServiceDetail {
    /** The compromised object beneath it that takes longest to recover in this scenario. Seconds. */
    heldUpBy: { objectId: string; description: string; seconds: number } | null
    toRecover: number
    /** Who is already responsible for the compromised objects, and how many have nobody. */
    teams: Array<{ id: number; team: string; objects: number }>
    unassigned: number
    withoutRunbook: string[]
    withoutCleanCopy: string[]
}
export interface ImpactScenarioOutcome {
    id: number
    name: string
    services: ImpactServiceOutcome[]
    summary: { operating: number; recoverable: number; recoverableWithGaps: number; afterCleaning?: number; blocked: number; unknown: number; noHierarchy?: number }
    /** The headline figures for this scenario. */
    kpis: { services: number; affected: number; blocked: number; pastTarget: number; pastMaximum: number; withoutTarget: number; longestSeconds: number | null }
    /** The applications of this scenario's hierarchy that are impacted (object ids). */
    applicationsImpacted?: string[]
    isDefault?: boolean
    /** The scenario impact is measured against: how the minimum viable company runs today. */
    isProduction?: boolean
    /** Lost locations it waits on, rebuilding in place. */
    cleanFirst?: string[]
    /** The customer's own words on the scenario. */
    useWhen?: string | null
    whenNotToUse?: string | null
    authorisedBy?: string | null
    /** The compromised locations that block it. */
    blockedBy?: string[]
    /** With parts planned: why parts cannot go where it sends them, and which use their fallback. */
    partReasons?: PartReason[]
    fallbacksUsed?: string[]
    parts?: Array<{ partId: number; partName: string; recoversTo: string; locationName?: string | null }>
    /** What it needs intact to work at all, and which of those are compromised. */
    dependsOn?: Array<{ kind: 'location' | 'technology' | 'object'; refId: string; name: string; compromised: boolean }>
    dependenciesHit?: string[]
    comparison?: ImpactScenarioComparison
}
export type ImpactVerdict = 'usable' | 'usable_with_gaps' | 'after_cleaning' | 'partly' | 'not_usable' | 'cannot_judge' | 'no_hierarchy' | 'not_needed'
export interface ImpactScenarioComparison {
    verdict: ImpactVerdict
    /** Business services impacted in any scenario, and how many of them this one can recover. */
    impacted: number
    canRecover: number
    /** Services it can recover only once lost locations are cleaned, and the slowest cleaning (seconds). */
    afterCleaning?: number
    cleanSeconds?: number | null
    blocked: number
    cannotJudge: number
    /** Impacted services this scenario has no hierarchy for. */
    noHierarchy: number
    withoutCleanCopy: string[]
    /** Null when the detail was not read. */
    withoutRunbook: string[] | null
    lastServiceBackSeconds: number | null
    /** When its last service is back, waiting for cleaning included; null when a cleaning time is not entered. */
    lastBackWithCleaningSeconds?: number | null
}
export interface ImpactReason { code: string; count?: number; total?: number; missing?: number; scenario?: string; locations?: string[]; seconds?: number | null }
export interface ImpactRecommendation { scenarioId: number | null; scenarioName?: string; code: 'recommended' | 'nothing_impacted' | 'none_usable'; reasons: ImpactReason[] }
export interface ImpactDecision { scenarioId: number; reason: string | null; recommendedScenarioId: number | null; decidedByName: string | null; decidedAt: string | null }
export interface ImpactOutcome {
    assessmentId: number
    compromised: number
    hitLocations: Array<{ id: number; name: string; cleanSeconds?: number | null }>
    /** The scenario marked production: what is affected is measured against it. Null: none marked. */
    production?: { id: number; name: string } | null
    /** The business services affected in production, each with how every scenario would recover it. */
    affected?: ImpactAffectedService[]
    /** The production hierarchy's business services that are not affected. */
    unaffected?: Array<{ objectId: string; description: string }>
    /** Which impacted services the scenarios were compared for: all unless some were chosen. */
    assessed?: { chosen: boolean; count: number; of: number; notImpacted: string[] }
    scenarios: ImpactScenarioOutcome[]
    recommendation?: ImpactRecommendation
    /** The scenario a person chose, if one has been recorded. */
    decision?: ImpactDecision | null
}

export interface ImpactRecoveryOption {
    scenarioId: number
    name: string
    isProduction: boolean
    state: ImpactState
    reason: ImpactServiceOutcome['reason']
    rolledUpRto: number | null
}
export interface ImpactAffectedService {
    objectId: string
    description: string
    rank?: number
    via: string[]
    compromisedBeneath: number
    /** Whether the recovery scenarios are compared for it: all impacted services unless some were chosen. */
    assessed?: boolean
    options: ImpactRecoveryOption[]
}

/** A recovery plan the AI assistant proposed (backend lib/recovery-plan.js), with every line checked. */
export interface RecoveryPlanLine { order: number; stackObjectId: string; scenarioId: number; covers: string[]; why: string; waitFor?: string | null }
export interface RecoveryPlanCheck { order: number; ok: boolean; problems: Array<{ code: string; objectIds?: string[]; states?: Array<string | null> }>; covers: string[]; alreadyRequested?: { requestId: string; recoverySet: number | null } }
export interface RecoveryPlan {
    id: number
    status: 'thinking' | 'ready' | 'failed'
    error: string | null
    requestedByName: string | null
    requestedAt: string | null
    completedAt: string | null
    model: { provider: string | null; id: string } | null
    plan: null | { approach: 'single_scenario' | 'separate_requests'; summary: string; lines: RecoveryPlanLine[]; notCovered: Array<{ objectId: string; why: string }>; assumptions: string[]; questions: string[] }
    checks: null | {
        lines: RecoveryPlanCheck[]
        uncovered: string[]
        plan: Array<{ code: string; objectIds?: string[] }>
        names: { scenarios: Record<string, string>; services: Record<string, string>; stacks: Record<string, string> }
        rulesRecommendation: number | null
    }
}

export interface ImpactMarkResult {
    dryRun: boolean
    objectStatus: 2 | 4
    /** Every object the selection means: everything at the locations, and the platforms everywhere. */
    objects: number
    atLocations: number
    onPlatforms: number
    applications: number
    changed: number
    servicesAffected: number
    /** A dry run of marking only: what it WOULD mean, per scenario, listing only the services that would not be operating. */
    outcome?: ImpactScenarioOutcome[]
}

/** An impact assessment: its own record, not a recovery set (14 Oct 2026). */
export interface ImpactAssessment {
    id: number
    heading: string
    description: string | null
    createdAt: string | null
    createdByName: string | null
    updatedAt: string | null
    updatedByName: string | null
    /** Objects marked compromised. */
    compromised: number
    /** The recovery sets that requests made from it were grouped into. */
    recoverySets: Array<{ id: number; name: string; requests: number }>
}
export interface ImpactChange { id: number; kind: 'created' | 'edited' | 'marked' | 'cleared' | 'noted' | 'decided' | 'requested' | string; summary: string; changedByName: string | null; changedAt: string | null }
export interface AssessmentObjectRow { id: string; objectID: string; description: string; objectStatus: 2 | 4; issueDescription: string; iconFilename: string | null }
export interface AssessmentStatusRow { objectID: string; description: string; analytics: number | null; iconFilename: string | null }
export interface ToRequestLine { objectId: string; name: string; objects: number; covers: string[]; sharedObjects: number; request: { requestId: string; recoverySet: number | null } | null }
export interface ToRequest { assessmentId: number; scenarioId: number | null; scenarioName: string | null; decided?: boolean; oneTree?: boolean; impacted: string[]; lines: ToRequestLine[]; covered: number }

type Envelope<T> = { success?: boolean; data: T }
const base = (id: number) => `/api/impact-assessments/${id}`

export const impactApi = {
    list: () => api.get<Envelope<ImpactAssessment[]>>('/api/impact-assessments'),
    get: (id: number) => api.get<Envelope<ImpactAssessment>>(base(id)),
    create: (body: { heading: string; description?: string }) => api.post<Envelope<ImpactAssessment>>('/api/impact-assessments', body),
    update: (id: number, body: { heading?: string; description?: string }) => api.put<Envelope<ImpactAssessment>>(base(id), body),
    remove: (id: number) => api.delete<Envelope<{ deleted: boolean }>>(base(id)),
    changes: (id: number) => api.get<Envelope<ImpactChange[]>>(`${base(id)}/changes`),
    scope: (id: number) => api.get<Envelope<ImpactScope>>(`${base(id)}/scope`),
    outcome: (id: number, detail = false) => api.get<Envelope<ImpactOutcome>>(`${base(id)}/outcome`, { params: detail ? { detail: 1 } : undefined }),
    mark: (id: number, body: ImpactSelection & { objectStatus: 2 | 4; dryRun?: boolean }) => api.post<Envelope<ImpactMarkResult>>(`${base(id)}/mark`, body),
    plan: (id: number) => api.get<Envelope<RecoveryPlan | null>>(`${base(id)}/plan`),
    askPlan: (id: number) => api.post<Envelope<RecoveryPlan>>(`${base(id)}/plan`, {}),
    setFocus: (id: number, objectIds: string[]) => api.put<Envelope<{ objectIds: string[] }>>(`${base(id)}/focus`, { objectIds }),
    setCleaning: (id: number, body: { locationId: number; hours: number | null }) => api.put<Envelope<{ locationId: number; cleanSeconds: number | null }>>(`${base(id)}/cleaning`, body),
    decide: (id: number, body: { scenarioId: number; reason?: string }) => api.post<Envelope<ImpactDecision>>(`${base(id)}/decision`, body),
    marks: (id: number, scenario: number) => api.get<Envelope<{ compromised: string[]; affected: string[]; markable: string[] }>>(`${base(id)}/marks`, { params: { scenario } }),
    objects: (id: number, params?: { search?: string; location?: number | null; team?: number | null }) => api.get<Envelope<Omit<AssessmentObjectRow, 'id'>[]>>(`${base(id)}/objects`, { params }),
    setObject: (id: number, objectId: string, body: { objectStatus?: 2 | 4; issueDescription?: string }) => api.patch<Envelope<unknown>>(`${base(id)}/objects/${encodeURIComponent(objectId)}`, body),
    objectStatus: (id: number, params?: { search?: string; filter?: string; scenario?: number | null }) => api.get<Envelope<{ data: Omit<AssessmentStatusRow, 'id'>[]; scenariosAffected: number; affectedScenarioIds: number[] }>>(`${base(id)}/object-status`, { params }),
    toRequest: (id: number, scenario?: number | null) => api.get<Envelope<ToRequest>>(`${base(id)}/to-request`, { params: scenario ? { scenario } : undefined }),
    group: (id: number, body: { requestId: string; setId?: number; newSet?: { name: string; description?: string } }) => api.post<Envelope<{ requestId: string; recoverySet: { id: number; name: string; new: boolean } }>>(`${base(id)}/group`, body),
}

export const emptySelection = (): ImpactSelection => ({ locationIds: [], classificationIds: [], technologyIds: [], objectIds: [] })
export const selectionIsEmpty = (s: ImpactSelection) => s.locationIds.length + s.classificationIds.length + s.technologyIds.length + s.objectIds.length === 0
const valid = (id: number | null | undefined): id is number => !!id && id > 0
/** Every list of one assessment sits under this key, so one change refreshes them all. */
export const assessmentKey = (id: number | null | undefined) => ['impact-assessments', id] as const

export function useImpactAssessments() {
    return useQuery({ queryKey: ['impact-assessments', 'list'], queryFn: async () => (await impactApi.list()).data.data ?? [] })
}
export function useCreateAssessment() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body: { heading: string; description?: string }) => (await impactApi.create(body)).data.data,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['impact-assessments', 'list'] }),
    })
}
export function useUpdateAssessment(id: number | null | undefined) {
    const refresh = useRefresh(id)
    return useMutation({
        mutationFn: async (body: { heading?: string; description?: string }) => (await impactApi.update(id as number, body)).data.data,
        onSuccess: refresh,
    })
}
export function useDeleteAssessment() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: number) => (await impactApi.remove(id)).data.data,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['impact-assessments'] }),
    })
}
export function useImpactChanges(id: number | null | undefined) {
    return useQuery({ queryKey: [...assessmentKey(id), 'changes'], queryFn: async () => (await impactApi.changes(id as number)).data.data ?? [], enabled: valid(id) })
}

export function useImpactScope(id: number | null | undefined) {
    return useQuery({ queryKey: [...assessmentKey(id), 'scope'], queryFn: async () => (await impactApi.scope(id as number)).data.data, enabled: valid(id) })
}

export function useImpactOutcome(id: number | null | undefined, { detail = false, enabled = true }: { detail?: boolean; enabled?: boolean } = {}) {
    return useQuery({
        queryKey: [...assessmentKey(id), 'outcome', detail ? 'detail' : 'summary'],
        queryFn: async () => (await impactApi.outcome(id as number, detail)).data.data,
        enabled: enabled && valid(id),
    })
}

/** What ticking these would mark, before it is applied. */
export function useImpactPreview(id: number | null | undefined, selection: ImpactSelection, objectStatus: 2 | 4) {
    return useQuery({
        queryKey: [...assessmentKey(id), 'preview', selection, objectStatus],
        queryFn: async () => (await impactApi.mark(id as number, { ...selection, objectStatus, dryRun: true })).data.data,
        enabled: valid(id) && !selectionIsEmpty(selection),
        staleTime: 0,
        // Keep the last answer on screen while the next tick is worked out, so the preview does not blink.
        placeholderData: keepPreviousData,
    })
}

/** Everything that reads an assessment's marks, refreshed after any change to them. */
function useRefresh(id: number | null | undefined) {
    const queryClient = useQueryClient()
    return () => Promise.all([
        queryClient.invalidateQueries({ queryKey: assessmentKey(id) }),
        queryClient.invalidateQueries({ queryKey: ['impact-assessments', 'list'] }),
    ])
}

/** The latest recovery plan; read again every few seconds while the assistant is working it out. */
export function useRecoveryPlan(id: number | null | undefined) {
    return useQuery({
        queryKey: [...assessmentKey(id), 'plan'],
        queryFn: async () => (await impactApi.plan(id as number)).data.data ?? null,
        enabled: valid(id),
        refetchInterval: (query) => ((query.state.data as RecoveryPlan | null | undefined)?.status === 'thinking' ? 4000 : false),
    })
}
export function useAskRecoveryPlan(id: number | null | undefined) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async () => (await impactApi.askPlan(id as number)).data.data,
        onSuccess: (plan) => {
            queryClient.setQueryData([...assessmentKey(id), 'plan'], plan)
            void queryClient.invalidateQueries({ queryKey: [...assessmentKey(id), 'changes'] })
        },
    })
}

/** Roughly how long a lost location takes to clean (hours; null clears it). */
export function useSetCleaning(id: number | null | undefined) {
    const refresh = useRefresh(id)
    return useMutation({
        mutationFn: async (body: { locationId: number; hours: number | null }) => (await impactApi.setCleaning(id as number, body)).data.data,
        onSuccess: refresh,
    })
}

/** Which impacted services the recovery scenarios are compared for; none = all of them. */
export function useSetFocus(id: number | null | undefined) {
    const refresh = useRefresh(id)
    return useMutation({
        mutationFn: async (objectIds: string[]) => (await impactApi.setFocus(id as number, objectIds)).data.data,
        onSuccess: refresh,
    })
}

export function useMarkImpact(id: number | null | undefined) {
    const refresh = useRefresh(id)
    return useMutation({
        mutationFn: async (body: ImpactSelection & { objectStatus: 2 | 4 }) => (await impactApi.mark(id as number, body)).data.data,
        onSuccess: refresh,
    })
}

/** Record the scenario a person has chosen. Nothing starts as a result. */
export function useRecordImpactDecision(id: number | null | undefined) {
    const refresh = useRefresh(id)
    return useMutation({
        mutationFn: async (body: { scenarioId: number; reason?: string }) => (await impactApi.decide(id as number, body)).data.data,
        onSuccess: refresh,
    })
}

// Every object: the object-level detail of an assessment.
export function useAssessmentObjects(id: number | null | undefined, params?: { search?: string; location?: number | null; team?: number | null }) {
    return useQuery({
        queryKey: [...assessmentKey(id), 'objects', params?.search ?? '', params?.location ?? '', params?.team ?? ''],
        queryFn: async (): Promise<AssessmentObjectRow[]> => ((await impactApi.objects(id as number, params)).data.data ?? []).map((r) => ({ ...r, id: r.objectID })),
        enabled: valid(id),
    })
}
export function useAssessmentObjectStatus(id: number | null | undefined, params?: { search?: string; filter?: 'all' | 'amber' | 'services' | 'amber-services'; scenario?: number | null }) {
    return useQuery({
        queryKey: [...assessmentKey(id), 'object-status', params?.search ?? '', params?.filter ?? 'all', params?.scenario ?? ''],
        queryFn: async () => {
            const body = (await impactApi.objectStatus(id as number, params)).data.data
            return { ...body, data: (body?.data ?? []).map((r) => ({ ...r, id: r.objectID })) as AssessmentStatusRow[] }
        },
        enabled: valid(id),
    })
}
export function useSetAssessmentObject(id: number | null | undefined) {
    const refresh = useRefresh(id)
    return useMutation({
        mutationFn: ({ id: objectId, ...body }: { id: string; objectStatus?: 2 | 4; issueDescription?: string }) => impactApi.setObject(id as number, objectId, body),
        onSuccess: refresh,
    })
}

export function useAssessmentFilterOptions() {
    return useQuery({
        queryKey: ['impact-assessments', 'filter-options'],
        queryFn: async () => (await api.get<Envelope<{ locations: Array<{ id: number; name: string }>; teams: Array<{ id: number; name: string }> }>>('/api/impact-assessments/filter-options')).data.data,
    })
}

// What to request, and grouping a request made from it.
export function useToRequest(id: number | null | undefined, scenario?: number | null) {
    return useQuery({ queryKey: [...assessmentKey(id), 'to-request', scenario ?? ''], queryFn: async () => (await impactApi.toRequest(id as number, scenario)).data.data, enabled: valid(id) })
}
export function useGroupRequest(id: number | null | undefined) {
    const queryClient = useQueryClient()
    const refresh = useRefresh(id)
    return useMutation({
        mutationFn: async (body: { requestId: string; setId?: number; newSet?: { name: string; description?: string } }) => (await impactApi.group(id as number, body)).data.data,
        onSuccess: async () => { await refresh(); await queryClient.invalidateQueries({ queryKey: ['recovery-sets'] }) },
    })
}
